/**
 * useMailRegisterDraft — owns the draft lifecycle for the mail register
 * wizard. Wraps TanStack Query with a debounced autosave to PUT /v1/drafts.
 *
 *   - `draft`        — server snapshot from the most recent fetch.
 *   - `payload`      — local working copy. Edits via `updatePayload(patch)`.
 *   - `setStep`      — server PUT immediately; updates step.
 *   - `submit`       — POST /v1/drafts/:id/submit. Returns success or one of
 *                      the F-006 prohibited-items failure shapes.
 *
 * Autosave debounce: 500 ms. We hold the latest payload in a ref and only
 * fire when both the timer elapses AND the payload differs from the last
 * value we PUT (deep equality via JSON.stringify — payloads are POJO-shaped
 * and small enough that this is fine).
 *
 * Hydration race fix (Gap 3):
 *   The previous implementation ran a useEffect on `draftQuery.data` that
 *   unconditionally reset `payload` and `step` on every server response,
 *   including PUT responses. This caused a race: the operator fills Step N+1
 *   fields → autosave fires → PUT response arrives → useEffect re-hydrates
 *   from server data → in-flight Step N+1 fields are wiped.
 *
 *   Fix: track `hydratedRef`. Hydration from the server only happens ONCE —
 *   on the initial load when the server data first arrives and local state is
 *   still at the default empty payload. Subsequent server responses (from PUT)
 *   do NOT update local `payload`/`step`; the server response only refreshes
 *   the `draft` object (for `draftRef`, `expiresAt`, etc.) via React Query's
 *   cache, which is used for display-only fields. Local operator edits always
 *   win over server data after the initial hydration.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  createDraft,
  getDraft,
  submitDraft as submitDraftCall,
  updateDraft,
} from '../../lib/api/mail-drafts';
import type {
  DraftPayload,
  DraftResponse,
  DraftStep,
  SubmitDraftResult,
} from '../../lib/api/types';

export const AUTOSAVE_DEBOUNCE_MS = 500;

interface UseMailRegisterDraftArgs {
  draftId: string | null;
  /** When draftId is null, useEffect creates a fresh draft on mount. */
  initialPayload?: DraftPayload;
  initialStep?: DraftStep;
}

export interface UseMailRegisterDraftResult {
  draftQuery: UseQueryResult<DraftResponse, Error>;
  draft: DraftResponse | undefined;
  payload: DraftPayload;
  step: DraftStep;
  isCreating: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  saveError: Error | null;
  submitError: Error | null;
  updatePayload: (patch: Partial<DraftPayload>) => void;
  setStep: (step: DraftStep) => Promise<void>;
  submit: (args?: { acknowledgedSoftWarnings?: boolean }) => Promise<SubmitDraftResult>;
  /** Force-flush any pending debounced save. Useful before submit. */
  flushPendingSave: () => Promise<void>;
}

const EMPTY_PAYLOAD: DraftPayload = {};

export function useMailRegisterDraft(
  args: UseMailRegisterDraftArgs,
): UseMailRegisterDraftResult {
  const { draftId, initialPayload = EMPTY_PAYLOAD, initialStep = 'SENDER' } = args;
  const qc = useQueryClient();

  // ---- Local working copy --------------------------------------------------
  const [payload, setPayload] = useState<DraftPayload>(initialPayload);
  const [step, setStepState] = useState<DraftStep>(initialStep);
  const lastSavedJsonRef = useRef<string>(JSON.stringify({ payload: initialPayload, step: initialStep }));
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef<Promise<void> | null>(null);

  /**
   * Latest-payload ref — always reflects the most-recent value passed to
   * setPayload, even before React has committed the re-render.
   *
   * Why two refs?
   *   payloadRef.current = payload  (line below) is updated at render time.
   *   latestPayloadRef.current      is updated INSIDE the setPayload functional
   *                                 updater, so it's current even within the
   *                                 same synchronous event-handler call that
   *                                 called updatePayload().
   *
   * This solves the setStep-after-updatePayload race: PricingStep calls
   *   updatePayload({ pricing })  →  schedules setPayload(callback)
   *   void setStep('REVIEW')      →  runs synchronously, needs latest payload
   * Without latestPayloadRef, setStep would use the pre-pricing payload and the
   * PUT to the server would be missing the locked quote.
   */
  const latestPayloadRef = useRef<DraftPayload>(initialPayload);
  const payloadRef = useRef<DraftPayload>(initialPayload);
  payloadRef.current = payload;

  /**
   * Tracks whether we have done the one-time initial hydration from the server.
   * Once set to true, subsequent server responses (from PUT autosave) do NOT
   * overwrite the operator's local edits. This prevents the hydration race where
   * a PUT response arrives while the operator is filling the next step and wipes
   * the in-progress fields.
   */
  const hydratedRef = useRef<boolean>(false);

  // ---- Server fetch -------------------------------------------------------
  const draftQuery = useQuery<DraftResponse, Error>({
    queryKey: ['mail-draft', draftId],
    enabled: draftId !== null,
    queryFn: async () => getDraft(draftId as string),
  });

  // Hydrate local state from server ONLY on the initial load (first data arrival).
  // After that, local state is the source of truth and PUT responses are ignored
  // for payload/step (they still update the React Query cache for display fields).
  useEffect(() => {
    const data = draftQuery.data;
    if (!data) {
      return;
    }
    if (hydratedRef.current) {
      // Already hydrated — don't overwrite operator's in-progress edits.
      return;
    }
    hydratedRef.current = true;
    const serverPayload = data.payload ?? EMPTY_PAYLOAD;
    const serverStep = data.step;
    latestPayloadRef.current = serverPayload;
    setPayload(serverPayload);
    setStepState(serverStep);
    lastSavedJsonRef.current = JSON.stringify({
      payload: serverPayload,
      step: serverStep,
    });
  }, [draftQuery.data]);

  // Reset hydration flag when draftId changes (user navigates to a different
  // draft). This ensures the new draft's server data is used for the initial
  // hydration instead of being silently skipped.
  useEffect(() => {
    hydratedRef.current = false;
    latestPayloadRef.current = initialPayload;
    setPayload(initialPayload);
    setStepState(initialStep);
    lastSavedJsonRef.current = JSON.stringify({ payload: initialPayload, step: initialStep });
    // note: deps intentionally narrow — rule not enabled in this config
  }, [draftId]);

  // ---- Auto-create on mount when no id supplied ---------------------------
  const createMutation = useMutation({
    mutationFn: async () =>
      createDraft({ payload: initialPayload, step: initialStep }),
  });

  useEffect(() => {
    if (draftId !== null) {
      return;
    }
    if (createMutation.isPending || createMutation.data) {
      return;
    }
    createMutation.mutate(undefined, {
      onSuccess: (created) => {
        qc.setQueryData(['mail-draft', created.id], created);
      },
    });
  }, [draftId]);

  const effectiveDraft = draftQuery.data ?? createMutation.data ?? undefined;

  // ---- Save mutation ------------------------------------------------------
  const saveMutation = useMutation({
    mutationFn: async (vars: { id: string; payload: DraftPayload; step: DraftStep }) =>
      updateDraft(vars.id, { payload: vars.payload, step: vars.step }),
    onSuccess: (updated) => {
      qc.setQueryData(['mail-draft', updated.id], updated);
    },
  });

  const performSave = useCallback(
    async (id: string, nextPayload: DraftPayload, nextStep: DraftStep): Promise<void> => {
      const json = JSON.stringify({ payload: nextPayload, step: nextStep });
      if (json === lastSavedJsonRef.current) {
        return;
      }
      lastSavedJsonRef.current = json;
      await saveMutation.mutateAsync({ id, payload: nextPayload, step: nextStep });
    },
    [saveMutation],
  );

  const scheduleSave = useCallback(
    (nextPayload: DraftPayload, nextStep: DraftStep) => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
      const id = effectiveDraft?.id;
      if (!id) {
        return;
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        const promise = performSave(id, nextPayload, nextStep).catch(() => undefined);
        pendingSaveRef.current = promise;
        promise.finally(() => {
          if (pendingSaveRef.current === promise) {
            pendingSaveRef.current = null;
          }
        });
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [effectiveDraft?.id, performSave],
  );

  const updatePayload = useCallback(
    (patch: Partial<DraftPayload>) => {
      // Helper to apply the deep-merge logic (shared between eager ref update
      // and the React functional updater).
      function applyPatch(prev: DraftPayload): DraftPayload {
        // Deep-merge nested objects (sender, receiver, package, pricing) so that
        // PartyForm's field-level onChange calls (which pass a partially-filled
        // sub-object built from a possibly-stale value closure) don't overwrite
        // sibling fields already set in a prior render.
        //
        // Scalar fields (serviceClass, acknowledgedSoftWarnings) fall through
        // to the plain assignment path since they are not objects.
        const next: DraftPayload = { ...prev };
        for (const _key of Object.keys(patch)) {
          const key = _key as keyof DraftPayload;
          const patchVal = patch[key];
          const prevVal = prev[key];
          const patchIsObj = patchVal !== null && typeof patchVal === 'object' && !Array.isArray(patchVal);
          const prevIsObj = prevVal !== null && typeof prevVal === 'object' && !Array.isArray(prevVal);
          if (patchIsObj && prevIsObj) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (next as any)[key] = { ...(prevVal as object), ...(patchVal as object) };
          } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (next as any)[key] = patchVal;
          }
        }
        return next;
      }

      // (A) Eagerly update latestPayloadRef BEFORE queuing the state update.
      //     This makes the latest accumulated payload immediately readable by
      //     setStep / flushPendingSave even when they are called synchronously
      //     in the same event as updatePayload (before React re-renders).
      //     Example: PricingStep calls updatePayload({ pricing }) then
      //     immediately calls setStep('REVIEW') — without this, setStep would
      //     save the pre-pricing payload and the backend 422s on submit.
      //
      // (A.1) Guard against late-arriving GET responses overwriting user edits.
      //       The first edit marks the draft as "user has started editing". A
      //       slow GET response (service-mail under load) arriving AFTER the
      //       user has begun filling fields would otherwise reset payload via
      //       the useEffect hydration path, wiping in-progress form data.
      //       Setting hydratedRef to true here prevents that re-hydration.
      hydratedRef.current = true;

      const next = applyPatch(latestPayloadRef.current);
      latestPayloadRef.current = next;

      // (B) Queue the React state update using the functional form so that
      //     rapid sequential calls (receiver form: name → phone → addressLine1
      //     → city → state) accumulate correctly even when React batches
      //     multiple setPayload calls into a single render pass. Each callback
      //     receives the result of the previous one as `prev`.
      setPayload((prev) => applyPatch(prev));

      scheduleSave(next, step);
    },
    [scheduleSave, step],
  );

  const setStep = useCallback(
    async (next: DraftStep): Promise<void> => {
      setStepState(next);
      const id = effectiveDraft?.id;
      if (!id) {
        return;
      }
      // Cancel pending debounce — step changes are explicit user actions, save now.
      // Use latestPayloadRef.current (updated inside the setPayload callback)
      // so that a setStep called synchronously after updatePayload — before
      // React re-renders — still picks up the newest fields.
      // Example: PricingStep calls updatePayload({ pricing }) then immediately
      // calls setStep('REVIEW') — without latestPayloadRef the pricing field
      // would be missing from the PUT request.
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      await performSave(id, latestPayloadRef.current, next);
    },
    [effectiveDraft?.id, performSave],
  );

  const flushPendingSave = useCallback(async (): Promise<void> => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
      const id = effectiveDraft?.id;
      if (id) {
        // Use latestPayloadRef.current for the same reason as setStep — flush
        // may be called immediately after updatePayload before a re-render.
        await performSave(id, latestPayloadRef.current, step);
      }
    }
    if (pendingSaveRef.current) {
      await pendingSaveRef.current;
    }
  }, [effectiveDraft?.id, step, performSave]);

  // ---- Submit mutation ----------------------------------------------------
  const submitMutation = useMutation({
    mutationFn: async (args: { id: string; acknowledgedSoftWarnings?: boolean }) =>
      submitDraftCall(args.id, { acknowledgedSoftWarnings: args.acknowledgedSoftWarnings }),
  });

  const submit = useCallback(
    async (
      submitArgs: { acknowledgedSoftWarnings?: boolean } = {},
    ): Promise<SubmitDraftResult> => {
      await flushPendingSave();
      const id = effectiveDraft?.id;
      if (!id) {
        throw new Error('No draft id available — cannot submit');
      }
      // Capture acknowledged flag in the payload so backend Zod has it too.
      // Use latestPayloadRef.current so we always get the most-recent payload.
      if (submitArgs.acknowledgedSoftWarnings === true) {
        await performSave(
          id,
          { ...latestPayloadRef.current, acknowledgedSoftWarnings: true },
          step,
        );
      }
      return submitMutation.mutateAsync({ id, acknowledgedSoftWarnings: submitArgs.acknowledgedSoftWarnings });
    },
    [effectiveDraft?.id, flushPendingSave, step, performSave, submitMutation],
  );

  // Clean up timer on unmount.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return useMemo(
    () => ({
      draftQuery,
      draft: effectiveDraft,
      payload,
      step,
      isCreating: createMutation.isPending,
      isSaving: saveMutation.isPending,
      isSubmitting: submitMutation.isPending,
      saveError: saveMutation.error,
      submitError: submitMutation.error,
      updatePayload,
      setStep,
      submit,
      flushPendingSave,
    }),
    [
      draftQuery,
      effectiveDraft,
      payload,
      step,
      createMutation.isPending,
      saveMutation.isPending,
      saveMutation.error,
      submitMutation.isPending,
      submitMutation.error,
      updatePayload,
      setStep,
      submit,
      flushPendingSave,
    ],
  );
}
